import { match } from "ts-pattern";
import type { FilterChip, Lookups } from "../../types";
import { renderFilterDetailList } from "../../components/FilterDetailList";
import { mapIdsToNames } from "../../utils/mapIdsToNames";
import { listNames } from "../../utils/listNames";
import type { ProductsSlice } from "./slice";

type LabelState = "loading" | "single" | "multiple";

function getLabelState(
  count: number,
  names: string[],
  lookups: Lookups | undefined,
  key: string
): LabelState {
  const lookupData = lookups?.[key];
  const isLoading = lookupData !== undefined && lookupData.length === 0;

  if (isLoading) return "loading";
  if (count === 1 && names[0]) return "single";
  return "multiple";
}

export function getChips(
  slice: ProductsSlice,
  setSlice: (slice: ProductsSlice) => void,
  lookups?: Lookups
): FilterChip[] {
  const chips: FilterChip[] = [];

  // Collect all included product IDs from sections
  const allIncludedProductIds = slice.sections.flatMap((s) => s.productIds);

  const inclusionCount = slice.sections.reduce((acc, s) => {
    const sectionCount =
      s.productIds.length +
      s.departmentIds.length +
      s.roleIds.length +
      (s.installAgeMonths ? 1 : 0) +
      (s.intensity ? 1 : 0);
    return acc + sectionCount;
  }, 0);

  // Chip for included products
  if (inclusionCount > 0) {
    const includedNames = lookups?.products?.length
      ? mapIdsToNames(allIncludedProductIds, lookups.products)
      : [];

    const labelState = getLabelState(
      allIncludedProductIds.length,
      includedNames,
      lookups,
      "products"
    );

    const label = match(labelState)
      .with("loading", () => "Loading...")
      .with("single", () => includedNames[0])
      .with("multiple", () => `${inclusionCount} selected`)
      .exhaustive();

    const tooltipContent =
      lookups?.products?.length && allIncludedProductIds.length > 0
        ? renderFilterDetailList("Products", includedNames)
        : undefined;

    const allNames = listNames(
      includedNames,
      inclusionCount - allIncludedProductIds.length
    );

    chips.push({
      id: "products",
      label: `Products: ${label}`,
      summaryLabel: allNames && `Products: ${allNames}`,
      filterKey: "products" as const,
      onRemove: () =>
        setSlice({
          sections: [
            {
              inclusionMethod: "ANY_PRESENT",
              productIds: [],
              departmentIds: [],
              roleIds: [],
              installAgeMonths: undefined,
              intensity: undefined,
            },
          ],
          excludedProductIds: slice.excludedProductIds,
        }),
      tooltipContent,
    });
  }

  // Chip for excluded products
  if (slice.excludedProductIds.length > 0) {
    const excludedNames = lookups?.products?.length
      ? mapIdsToNames(slice.excludedProductIds, lookups.products)
      : [];

    const labelState = getLabelState(
      slice.excludedProductIds.length,
      excludedNames,
      lookups,
      "products"
    );

    const label = match(labelState)
      .with("loading", () => "Loading...")
      .with("single", () => excludedNames[0])
      .with("multiple", () => `${slice.excludedProductIds.length} selected`)
      .exhaustive();

    const tooltipContent = lookups?.products?.length
      ? renderFilterDetailList("Products", excludedNames)
      : undefined;

    const allNames = listNames(excludedNames);

    chips.push({
      id: "excludedProducts",
      label: `Excluded Products: ${label}`,
      summaryLabel: allNames && `Excluded Products: ${allNames}`,
      filterKey: "products" as const,
      onRemove: () =>
        setSlice({
          ...slice,
          excludedProductIds: [],
        }),
      tooltipContent,
    });
  }

  return chips;
}

export function getCount(slice: ProductsSlice): number {
  return (
    slice.sections.reduce((acc, s) => {
      const sectionCount =
        s.productIds.length +
        s.departmentIds.length +
        s.roleIds.length +
        (s.installAgeMonths ? 1 : 0);
      return acc + sectionCount;
    }, 0) + slice.excludedProductIds.length
  );
}
